import imgSaudeMental from '../assets/img/blog/saude-mental.webp';
import imgAnsiedade from '../assets/img/blog/ansiedade.webp';
import imgDesenvolvimentoInfantil from '../assets/img/blog/desenvolvimento-infantil.webp';
// Definição do tipo para posts do blog
export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  author: string;
  excerpt: string;
  content: string | unknown[]; // string = markdown/HTML (fallback), unknown[] = Portable Text (Sanity)
  coverImage: string;
  tags: string[];
}

// Dados dos posts do blog
export const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'A importância da saúde mental no dia a dia',
    slug: 'importancia-saude-mental-dia-a-dia',
    date: '2025-06-01',
    author: 'Eliane Forti Fernandes',
    excerpt: 'Entenda como cuidar da saúde mental pode transformar sua qualidade de vida e bem-estar geral.',
    content: `
      <p>A saúde mental é um componente essencial do nosso bem-estar geral, influenciando como pensamos, sentimos e agimos em nosso cotidiano. Apesar de sua importância, muitas vezes negligenciamos os cuidados com nossa mente, priorizando apenas aspectos físicos da saúde.</p>
      
      <h3>Por que devemos priorizar a saúde mental?</h3>
      
      <p>Nossa mente e corpo estão intrinsecamente conectados. Quando experimentamos estresse prolongado, ansiedade ou outros desafios emocionais, nosso corpo responde com sintomas físicos como dores de cabeça, problemas digestivos e até mesmo comprometimento do sistema imunológico.</p>
      
      <p>Cuidar da saúde mental não significa apenas tratar problemas existentes, mas também adotar práticas preventivas que fortaleçam nossa resiliência emocional e capacidade de lidar com os desafios da vida.</p>
      
      <h3>Práticas diárias para promover bem-estar mental</h3>
      
      <ul>
        <li><strong>Mindfulness e meditação:</strong> Dedique alguns minutos do seu dia para praticar atenção plena, observando seus pensamentos sem julgamento.</li>
        <li><strong>Atividade física regular:</strong> O exercício libera endorfinas, conhecidas como "hormônios da felicidade", que melhoram o humor e reduzem o estresse.</li>
        <li><strong>Conexões sociais:</strong> Manter relacionamentos significativos e uma rede de apoio é fundamental para nossa saúde emocional.</li>
        <li><strong>Limites saudáveis:</strong> Aprender a dizer "não" quando necessário e estabelecer limites claros em relacionamentos e no trabalho.</li>
        <li><strong>Autocuidado:</strong> Reservar tempo para atividades que tragam prazer e relaxamento é essencial, não um luxo.</li>
      </ul>
      
      <h3>Quando buscar ajuda profissional</h3>
      
      <p>É importante reconhecer que, assim como buscamos médicos para cuidar da saúde física, profissionais de saúde mental são essenciais quando enfrentamos desafios emocionais. A psicoterapia oferece um espaço seguro para explorar pensamentos, sentimentos e comportamentos, desenvolvendo estratégias eficazes para lidar com dificuldades.</p>
      
      <p>Buscar ajuda não é sinal de fraqueza, mas de coragem e autocuidado. Se você percebe que seus desafios emocionais estão afetando sua qualidade de vida, relacionamentos ou capacidade de realizar atividades diárias, considere conversar com um psicólogo.</p>
      
      <h3>Conclusão</h3>
      
      <p>A saúde mental merece a mesma atenção e cuidado que dedicamos à saúde física. Ao integrar práticas de bem-estar emocional em nossa rotina e buscar apoio quando necessário, investimos em uma vida mais equilibrada, satisfatória e plena.</p>
    `,
    coverImage: imgSaudeMental,
    tags: ['saúde mental', 'bem-estar', 'autocuidado']
  },
  {
    id: '2',
    title: 'Como lidar com a ansiedade em momentos de incerteza',
    slug: 'como-lidar-ansiedade-momentos-incerteza',
    date: '2025-05-15',
    author: 'Eliane Forti Fernandes',
    excerpt: 'Estratégias práticas para gerenciar a ansiedade quando o futuro parece incerto.',
    content: `
      <p>A ansiedade é uma resposta natural do nosso organismo diante de situações percebidas como ameaçadoras ou incertas. Em doses moderadas, ela pode até ser útil, nos mantendo alertas e preparados. No entanto, quando se torna excessiva ou constante, pode comprometer significativamente nossa qualidade de vida.</p>
      
      <p>Em momentos de grande incerteza, como mudanças na carreira, relacionamentos ou crises globais, é comum que os níveis de ansiedade se elevem. Neste artigo, compartilho algumas estratégias que podem ajudar a navegar por esses períodos desafiadores.</p>
      
      <h3>Reconheça e acolha seus sentimentos</h3>
      
      <p>O primeiro passo para lidar com a ansiedade é reconhecê-la sem julgamentos. Muitas vezes, tentamos reprimir ou ignorar emoções desconfortáveis, o que pode intensificá-las. Permita-se sentir o que está sentindo, lembrando que emoções são temporárias e fluidas.</p>
      
      <h3>Foque no presente</h3>
      
      <p>A ansiedade frequentemente nos leva a viver no futuro, antecipando cenários negativos que podem nunca se concretizar. Práticas de mindfulness nos ajudam a ancorar no momento presente, reduzindo o fluxo de pensamentos ansiosos.</p>
      
      <p>Uma técnica simples é o exercício 5-4-3-2-1: identifique 5 coisas que você pode ver, 4 que pode tocar, 3 que pode ouvir, 2 que pode cheirar e 1 que pode provar. Este exercício ajuda a reconectar com o momento presente através dos sentidos.</p>
      
      <h3>Estabeleça uma rotina de autocuidado</h3>
      
      <p>Em períodos de incerteza, manter uma rotina pode proporcionar uma sensação de controle e estabilidade. Inclua em sua rotina:</p>
      
      <ul>
        <li>Sono adequado (7-8 horas por noite)</li>
        <li>Alimentação balanceada</li>
        <li>Atividade física regular</li>
        <li>Momentos de lazer e desconexão</li>
        <li>Práticas de relaxamento (meditação, respiração profunda, yoga)</li>
      </ul>
      
      <h3>Limite o consumo de informações</h3>
      
      <p>O excesso de informações, especialmente notícias negativas, pode alimentar a ansiedade. Estabeleça horários específicos para se atualizar e evite o consumo de notícias antes de dormir.</p>
      
      <h3>Cultive conexões significativas</h3>
      
      <p>O isolamento tende a intensificar sentimentos de ansiedade. Manter-se conectado com pessoas que oferecem suporte emocional é fundamental. Compartilhe seus sentimentos com pessoas de confiança – muitas vezes, apenas expressar o que sentimos já traz alívio.</p>
      
      <h3>Quando buscar ajuda profissional</h3>
      
      <p>Se a ansiedade estiver comprometendo significativamente seu bem-estar, relacionamentos ou capacidade de realizar atividades cotidianas, é importante buscar ajuda profissional. A psicoterapia oferece um espaço seguro para explorar os gatilhos da ansiedade e desenvolver estratégias personalizadas para lidar com ela.</p>
      
      <p>Lembre-se: buscar ajuda é um ato de coragem e autocuidado, não de fraqueza.</p>
      
      <h3>Conclusão</h3>
      
      <p>A incerteza faz parte da vida, e aprender a navegar por ela com mais serenidade é uma habilidade valiosa. Ao implementar estas estratégias e buscar apoio quando necessário, você estará fortalecendo sua resiliência emocional para enfrentar não apenas os desafios atuais, mas também os futuros.</p>
    `,
    coverImage: imgAnsiedade,
    tags: ['ansiedade', 'incerteza', 'bem-estar emocional']
  },
  {
    id: '3',
    title: 'O papel dos pais no desenvolvimento emocional das crianças',
    slug: 'papel-pais-desenvolvimento-emocional-criancas',
    date: '2025-04-20',
    author: 'Eliane Forti Fernandes',
    excerpt: 'Como os pais podem contribuir para o desenvolvimento de crianças emocionalmente saudáveis e resilientes.',
    content: `
      <p>O desenvolvimento emocional saudável durante a infância estabelece as bases para o bem-estar psicológico ao longo da vida. Os pais e cuidadores desempenham um papel fundamental nesse processo, servindo como primeiros "professores emocionais" das crianças.</p>
      
      <h3>A importância da validação emocional</h3>
      
      <p>Validar as emoções das crianças significa reconhecer e aceitar seus sentimentos, independentemente de concordarmos com eles ou não. Quando uma criança expressa tristeza, raiva ou medo, frases como "não fique triste" ou "não há motivo para ter medo" podem transmitir a mensagem de que certas emoções são inaceitáveis.</p>
      
      <p>Em vez disso, podemos dizer: "Vejo que você está triste/com raiva/com medo. Está tudo bem sentir isso." Esta abordagem ajuda a criança a reconhecer e nomear suas emoções, primeiro passo para aprender a gerenciá-las.</p>
      
      <h3>Modelando comportamentos emocionais saudáveis</h3>
      
      <p>As crianças aprendem mais observando do que ouvindo instruções. A maneira como os pais expressam e lidam com suas próprias emoções serve como poderoso modelo para os filhos.</p>
      
      <p>Isso não significa esconder emoções negativas, mas demonstrar formas construtivas de expressá-las e gerenciá-las. Por exemplo, um pai pode dizer: "Estou me sentindo frustrado agora e preciso de um momento para me acalmar antes de conversarmos."</p>
      
      <h3>Criando um ambiente seguro para a expressão emocional</h3>
      
      <p>Crianças precisam sentir que é seguro expressar toda a gama de emoções sem medo de rejeição ou punição. Um ambiente emocionalmente seguro é aquele em que:</p>
      
      <ul>
        <li>As emoções são nomeadas e discutidas abertamente</li>
        <li>Há tempo e espaço para processá-las</li>
        <li>Comportamentos inadequados são corrigidos sem invalidar os sentimentos subjacentes</li>
        <li>As diferenças individuais na expressão emocional são respeitadas</li>
      </ul>
      
      <h3>Ensinando habilidades de regulação emocional</h3>
      
      <p>Regular emoções é uma habilidade que precisa ser aprendida e praticada. Os pais podem ajudar seus filhos a desenvolver esta capacidade através de:</p>
      
      <ul>
        <li>Técnicas de respiração adequadas à idade</li>
        <li>Identificação de sinais corporais associados a diferentes emoções</li>
        <li>Estratégias de acalmamento (contar até 10, abraçar um bichinho de pelúcia, desenhar)</li>
        <li>Conversas sobre emoções em momentos de calma, não apenas durante crises</li>
      </ul>
      
      <h3>O equilíbrio entre proteção e autonomia</h3>
      
      <p>É natural querermos proteger nossos filhos de experiências dolorosas. No entanto, superproteger pode privá-los de oportunidades valiosas para desenvolver resiliência emocional.</p>
      
      <p>Permitir que as crianças enfrentem desafios apropriados à sua idade, oferecendo apoio sem resolver todos os problemas por elas, ajuda a construir confiança em sua capacidade de lidar com adversidades.</p>
      
      <h3>Quando buscar ajuda profissional</h3>
      
      <p>Algumas crianças podem enfrentar desafios emocionais que requerem apoio adicional. Sinais de que pode ser hora de buscar orientação profissional incluem:</p>
      
      <ul>
        <li>Mudanças persistentes no comportamento ou humor</li>
        <li>Dificuldades significativas para lidar com transições ou frustrações</li>
        <li>Isolamento social</li>
        <li>Comportamentos regressivos persistentes</li>
        <li>Medos intensos ou preocupações que interferem no funcionamento diário</li>
      </ul>
      
      <h3>Conclusão</h3>
      
      <p>Criar filhos emocionalmente saudáveis não significa criar filhos que nunca sofrem, mas sim crianças que desenvolvem recursos internos para navegar pelo espectro completo de experiências emocionais humanas.</p>
      
      <p>Ao validar emoções, modelar comportamentos saudáveis e ensinar habilidades de regulação emocional, os pais oferecem aos filhos ferramentas valiosas que os beneficiarão por toda a vida.</p>
    `,
    coverImage: imgDesenvolvimentoInfantil,
    tags: ['parentalidade', 'desenvolvimento infantil', 'inteligência emocional']
  }
];
